import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Workflow as WorkflowIcon, Loader2, Copy, Trash2, Power, Play, LayoutTemplate } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { TEMPLATES } from "@/lib/workflowBlocks";

const fmt = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const badge = { active: "bg-emerald-100 text-emerald-700", inactive: "bg-slate-100 text-slate-600", draft: "bg-blue-100 text-blue-700" };

export default function Workflows() {
  const [items, setItems] = useState(null);
  const [showTpl, setShowTpl] = useState(false);
  const nav = useNavigate();
  const load = () => api.get("/workflows").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const create = async (payload, goEdit = true) => {
    try { const r = await api.post("/workflows", payload || {}); if (goEdit) nav(`/workflows/${r.data.id}`); else load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const fromTemplate = async (tpl) => {
    const { nodes, edges } = tpl.build();
    setShowTpl(false);
    await create({ name: tpl.name, nodes, edges });
  };
  const toggle = async (w) => { try { await api.post(`/workflows/${w.id}/status`, { status: w.status === "active" ? "inactive" : "active" }); toast.success("Updated"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const dup = async (w) => { try { await api.post(`/workflows/${w.id}/duplicate`); toast.success("Duplicated"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const del = async (w) => { if (!window.confirm(`Delete "${w.name}"?`)) return; try { await api.delete(`/workflows/${w.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div data-testid="workflows-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader title="Workflows" subtitle="Visual automation for nurture, follow-up and qualification" />
        <div className="flex gap-2 relative">
          <Button variant="outline" onClick={() => setShowTpl((v) => !v)} data-testid="import-template-btn"><LayoutTemplate className="w-4 h-4 mr-1" />Import Template</Button>
          <Button style={{ background: "#4f46e5" }} onClick={() => create()} data-testid="new-workflow-btn"><Plus className="w-4 h-4 mr-1" />New Workflow</Button>
          {showTpl && (
            <div className="absolute right-0 top-11 z-20 w-72 bg-white dark:bg-[#15122e] border rounded-xl shadow-lg p-2" data-testid="template-list">
              {TEMPLATES.map((t) => (
                <button key={t.id} onClick={() => fromTemplate(t)} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5" data-testid={`new-tpl-${t.id}`}>
                  <div className="text-sm font-medium">{t.name}</div><div className="text-[11px] text-slate-400">{t.desc}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {items === null ? (
        <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-500" /></div>
      ) : items.length === 0 ? (
        <Card className="border shadow-none p-12 text-center mt-4" data-testid="workflows-empty">
          <WorkflowIcon className="w-10 h-10 mx-auto text-indigo-300 mb-3" />
          <div className="font-semibold mb-1">No workflows yet</div>
          <div className="text-sm text-slate-500 mb-4">Start from scratch or pick a ready-made template.</div>
          <div className="flex justify-center gap-2"><Button onClick={() => create()} style={{ background: "#4f46e5" }}><Plus className="w-4 h-4 mr-1" />New Workflow</Button><Button variant="outline" onClick={() => setShowTpl(true)}>Browse templates</Button></div>
        </Card>
      ) : (
        <Card className="border shadow-none mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-400 border-b">
              <th className="px-4 py-2.5">Workflow</th><th className="px-4 py-2.5">Trigger</th><th className="px-4 py-2.5">Actions</th>
              <th className="px-4 py-2.5">Leads Active</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Last Modified</th><th className="px-4 py-2.5 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {items.map((w) => (
                <tr key={w.id} className="border-b hover:bg-slate-50 dark:hover:bg-white/5" data-testid={`workflow-row-${w.id}`}>
                  <td className="px-4 py-3"><button className="font-medium hover:text-indigo-600" onClick={() => nav(`/workflows/${w.id}`)} data-testid={`workflow-open-${w.id}`}>{w.name}</button></td>
                  <td className="px-4 py-3 text-slate-500">{w.trigger || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{w.actions_count}</td>
                  <td className="px-4 py-3 text-slate-500">{w.leads_active || 0}</td>
                  <td className="px-4 py-3"><span className={`capitalize text-xs px-2 py-0.5 rounded-full ${badge[w.status] || badge.draft}`}>{w.status}</span></td>
                  <td className="px-4 py-3 text-slate-400">{fmt(w.updated_at)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="mr-1.5" onClick={() => nav(`/workflows/${w.id}`)} data-testid={`workflow-edit-${w.id}`}>Edit</Button>
                    <Button size="sm" variant="outline" className="mr-1.5" onClick={() => toggle(w)} data-testid={`workflow-toggle-${w.id}`}>{w.status === "active" ? <Power className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}</Button>
                    <Button size="sm" variant="outline" className="mr-1.5" onClick={() => dup(w)} data-testid={`workflow-duplicate-${w.id}`}><Copy className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => del(w)} data-testid={`workflow-delete-${w.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
